import { TableCell, TableRow, Typography, Stack, FormHelperText } from "@mui/material";
import { useModifyMapFieldMutation } from "api/api-maps";
import VariableDropdown from "components/Common/VariableDropdown";
import VariableInput from "components/Common/VariableInput";
import { IRiskScoring } from "components/Risk/defaults";
import { useFormikContext } from "formik";
import toCamelCase from "helpers/camelCase";
import { toast } from "react-toastify";
import type { RiskCategory } from "types";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import { useRef, useState } from "react";
import { PERMISSIONS } from "permissions";
import { useAppSelector } from "redux/hooks";
// Import scoring matrices
import { 
  getFieldScore, 
  getSupplierFieldScore, 
  getTradingRelationshipFieldScore 
} from "../scoringMatrices";

interface RiskCategoryDescription {
  map_metadata_id: string;
  value_type: string;
  name: string;
  value: number;
}

const Category = ({
  category,
  type,
  mapId,
  disabled,
}: {
  category: RiskCategory;
  type: string;
  mapId: string;
  disabled: boolean;
}) => {
  const formikProps = useFormikContext<IRiskScoring>();
  
  // Check if current user is Risk Admin
  const USER_PERMISSIONS = useAppSelector(
    (state) => state.appState.user_permission
  );
  const IS_RISK_TEAM_MEMBER = USER_PERMISSIONS?.some((permission) => 
    permission === PERMISSIONS.map_access_risk ||
    permission === PERMISSIONS.map_view_risk ||
    permission === PERMISSIONS.show_risk_management ||
    permission === PERMISSIONS.approval_level_head_risk
  );
  
  const metadataIddField = `${type}.${toCamelCase(category.item)}.id`;
  const descriptionField = `${type}.${toCamelCase(category.item)}.description`;
  const scoreField = `${type}.${toCamelCase(category.item)}.score`;
  const value =
    formikProps.values?.[type]?.[toCamelCase(category.item)]?.description;
  const [modifyMapField, { isLoading }] = useModifyMapFieldMutation();
  const previousValidScore = useRef<string | number | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [blurCount, setBlurCount] = useState(0);

  // Check if this is a negative adjustment
  const isNegativeAdjustment = type === "adjustment" && 
    category.item.toLowerCase().includes("negative");
  
  // Check if this is a positive adjustment
  const isPositiveAdjustment = type === "adjustment" && 
    category.item.toLowerCase().includes("positive");

  // Calculate score based on selected description and category type
  const calculateScoreFromDescription = (description: string, categoryItem: string): number => {
    let score = 0;
    
    switch (type) {
      case "buyer":
        score = getFieldScore(categoryItem, description);
        break;
      case "supplier":
        score = getSupplierFieldScore(categoryItem, description);
        break;
      case "tradingRelationship":
        score = getTradingRelationshipFieldScore(categoryItem, description);
        break;
      default:
        // Fallback to existing logic if no scoring matrix found
        score = category.item_descriptions.find(
          (item) => item.name === description
        )?.value ?? 0;
    }
    
    return score;
  };

  const setter = (value: string | undefined) => {
    // Calculate score based on the scoring matrices
    const calculatedScore = value ? calculateScoreFromDescription(value, category.item) : 0;
    
    // Use calculated score if available, otherwise fallback to existing logic
    const score = calculatedScore || (category.item_descriptions.find(
      (item) => item.name === value
    )?.value ?? 0);
    
    const mapMetadataId = category.map_metadata_id;
    if (mapMetadataId) {
      formikProps.setFieldValue(metadataIddField, mapMetadataId);
      formikProps.setFieldValue(descriptionField, value ?? '');
      formikProps.setFieldValue(scoreField, score);
    }
  };

  const currentSelectionMeta = category?.current_risk_selection_metadata;
  const formikItem = formikProps.values?.[type]?.[toCamelCase(category.item)];

  // Get display value with fallback logic
  const displayValue = value || 
    currentSelectionMeta?.current_risk_rating || 
    formikItem?.description || 
    '';

  // Calculate formatted score for display
  const currentScore = formikItem?.score ?? currentSelectionMeta?.current_risk_rating_score ?? 0;
  const formattedScore = typeof currentScore === 'number' ? 
    (currentScore === 0 ? '0' : currentScore.toString()) : 
    (currentScore || '0');

  // Special categories that should always show the backend score (even if zero)
  const isSpecialCategory = [
    "Buyer Assessment",
    "Supplier Assessment", 
    "Trading Relationship"
  ].includes(category.item);

  // Always show 0 if the score is zero, don't hide it
  const displayScore = isSpecialCategory
    ? (typeof currentSelectionMeta?.current_risk_rating_score === 'number' ? currentSelectionMeta.current_risk_rating_score : 0)
    : (typeof formikItem?.score === 'number' ? formikItem.score : (typeof currentSelectionMeta?.current_risk_rating_score === 'number' ? currentSelectionMeta.current_risk_rating_score : 0));

  const handleScoreChange = (value: any) => {
    setError(null);
    setBlurCount(0);
    // Convert to string first to handle any input type
    const strValue = value?.toString() || '0';
    
    // If empty string, set to 0
    if (strValue === '') {
      formikProps.setFieldValue(scoreField, 0);
      previousValidScore.current = 0;
      return;
    }
    
    // Check if the value is a valid float with up to 2 decimal places
    if (!/^-?\d*\.?\d{0,2}$/.test(strValue)) {
      formikProps.setFieldValue(scoreField, 0);
      setError("Please enter a valid number with up to 2 decimal places");
      return;
    }
    
    const numValue = parseFloat(strValue);
    
    if (isNaN(numValue)) {
      formikProps.setFieldValue(scoreField, 0);
      setError("Please enter a valid number");
      return;
    }
    
    if (numValue < 0 || numValue > 10) {
      formikProps.setFieldValue(scoreField, 0);
      setError("Value must be between 0.00 and 10.00");
      return;
    }
    
    // Round to 2 decimal places
    const roundedValue = Math.round(numValue * 100) / 100;
    
    // Get the current name value
    const currentName = formikProps.values?.[type]?.[
      toCamelCase(category.item)
    ]?.description ?? '';
    
    // Set the score
    formikProps.setFieldValue(scoreField, roundedValue);
    previousValidScore.current = roundedValue;
    
    // Update the API immediately for adjustment types
    if (type === "adjustment") {
      const finalValue = isNegativeAdjustment ? -roundedValue : roundedValue;
      modifyMapField({
        map_name: mapId,
        data: {
          map_metadata_id: category.map_metadata_id ?? "",
          value: {
            name: currentName,
            value: finalValue,
          },
        },
      })
        .unwrap()
        .then(() => {
          toast("Score updated", { type: "success" });
        })
        .catch((e) =>
          toast(`An error occurred: ${e?.message}`, { type: "error" })
        );
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    if (error) {
      setBlurCount((prev) => prev + 1);
      if (blurCount >= 1) {
        // Restore the original score from current_risk_selection_metadata
        const originalScore = currentSelectionMeta?.current_risk_rating_score ?? 0;
        formikProps.setFieldValue(scoreField, originalScore);
        previousValidScore.current = originalScore;
        setError(null);
      }
    } else {
      // If no error, update the API with the current value
      const currentName = formikProps.values?.[type]?.[toCamelCase(category.item)]?.description ?? '';
      if (currentName) {
        const finalValue = isNegativeAdjustment ? -formattedScore : formattedScore;
        modifyMapField({
          map_name: mapId,
          data: {
            map_metadata_id: category.map_metadata_id ?? "",
            value: {
              name: currentName,
              value: finalValue,
            },
          },
        })
          .unwrap()
          .then(() => {
            toast("Values updated", { type: "success" });
          })
          .catch((e) =>
            toast(`An error occurred: ${e?.message}`, { type: "error" })
          );
      }
    }
  };

  return (
    <TableRow>
      <TableCell>
        <Typography fontWeight="bold">
          {category.item}
        </Typography>
      </TableCell>
      <TableCell>
        {type !== "adjustment" ? (
          <VariableDropdown
            name={descriptionField}
            keyValue="name"
            value={displayValue}
            data={category.item_descriptions}
            setValue={setter}
            handleSave={(value) => {
              const canUpdate = currentSelectionMeta
                ? formikItem?.description !==
                  currentSelectionMeta?.current_risk_rating
                : true;
              canUpdate &&
                modifyMapField({
                  map_name: mapId,
                  data: {
                    map_metadata_id: formikItem?.id,
                    value: {
                      name: value,
                      value: formikItem?.score,
                    },
                  },
                })
                  .unwrap()
                  .then(() => {
                    toast(`Set ${type}'s ${category.item} as ${value}`, {
                      type: "success",
                    });
                  })
                  .catch((e) =>
                    toast(`An error occured: ${e?.message}`, {
                      type: "error",
                    })
                  );
            }}
            disabled={category.item === "Country Risk" ? !IS_RISK_TEAM_MEMBER : disabled}
            noHover={category.item === "Country Risk" ? !IS_RISK_TEAM_MEMBER : disabled}
          />
        ) : (
          <VariableInput
            name={descriptionField}
            label={undefined}
            value={displayValue}
            type="text"
            handleSave={(value) =>
              modifyMapField({
                map_name: mapId,
                data: {
                  map_metadata_id: category.map_metadata_id ?? "",
                  value: {
                    name: value ?? '',
                    value: formattedScore,
                  },
                },
              })
                .unwrap()
                .then(() => {
                  toast(`Set ${category.item} as ${value}`, {
                    type: "success",
                  });
                })
                .catch((e) =>
                  toast(`An error occured: ${e?.message}`, { type: "error" })
                )
            }
            disabled={disabled}
            noHover={disabled}
            {...formikProps}
          />
        )}
      </TableCell>
      <TableCell>
        {type === "adjustment" ? (
          <Stack direction="row" spacing={1} alignItems="center">
            {isNegativeAdjustment && <RemoveIcon color="error" />}
            {isPositiveAdjustment && <AddIcon color="success" />}
            <Stack spacing={0.5}>
              <Stack direction="row" spacing={1} alignItems="center">
                <div 
                  onFocus={() => {
                    setIsFocused(true);
                    setError(null);
                    setBlurCount(0);
                  }}
                  onBlur={handleBlur}
                >
                  <VariableInput
                    name={scoreField}
                    label={undefined}
                    value={formattedScore}
                    type="number"
                    min="0.00"
                    max="10.00"
                    step="0.01"
                    handleSave={handleScoreChange}
                    disabled={disabled}
                    noHover={disabled}
                    {...formikProps}
                  />
                </div>
              </Stack>
              {error && (
                <FormHelperText error sx={{ mt: 0, ml: 1 }}>
                  {error}
                </FormHelperText>
              )}
            </Stack>
          </Stack>
        ) : (
          // Display calculated score for all non-adjustment categories
          <Typography fontWeight="bold" color="primary">
            {formattedScore}
          </Typography>
        )}
      </TableCell>
    </TableRow>
  );
};

export default Category;
